import { SUPPORT_EMAIL } from '@psyomics/ui-common';
import { InputHTMLAttributes, useCallback, useEffect, useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Input } from '../../../atoms/input/input';
import { Label } from '../../../atoms/label/label';
import { Text } from '../../../atoms/text/text';
import { createSubmitHandler } from '../handle-submit';

import { BaseCaptureFormProps, FormLayout, useCollectedErrors } from './verification.shared';

const RETRY_DELAY_SECONDS = 30;
const MILLIS_PER_SECOND = 1000;

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const useRetryCountdown = (durationSeconds: number) => {
  const [secondsRemaining, setSecondsRemaining] = useState(durationSeconds);
  const isCountingDown = secondsRemaining > 0;

  useEffect(() => {
    if (!isCountingDown) return undefined;

    const intervalId = setInterval(() => {
      setSecondsRemaining((remaining) => Math.max(remaining - 1, 0));
    }, MILLIS_PER_SECOND);

    return () => clearInterval(intervalId);
  }, [isCountingDown]);

  const restart = useCallback(() => setSecondsRemaining(durationSeconds), [durationSeconds]);

  return { restart, secondsRemaining };
};

const InlineLinkButton = ({
  children,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
}) => (
  <button className="text-blue-600 underline cursor-pointer" onClick={onClick} type="button">
    {children}
  </button>
);

const getSecondLink = ({
  onChangeNumber,
  onStartAgain,
  showSupportContact,
  t,
}: {
  onChangeNumber?: () => void;
  onStartAgain?: () => void;
  showSupportContact: boolean;
  t: TranslateFn;
}) => {
  if (onChangeNumber)
    return <InlineLinkButton onClick={onChangeNumber}>{t('page.mfaSetup.code.changeNumber')}</InlineLinkButton>;
  if (onStartAgain)
    return <InlineLinkButton onClick={onStartAgain}>{t('page.mfaSetup.code.startAgain')}</InlineLinkButton>;
  if (showSupportContact)
    return (
      <Trans
        components={{
          linkTag: (
            <a className="text-blue-500 underline" href={`mailto:${SUPPORT_EMAIL}`}>
              {SUPPORT_EMAIL}
            </a>
          ),
        }}
        i18nKey={'page.mfaSetup.code.contactSupport'}
      />
    );
  return undefined;
};

const HaventReceivedCodeHelpText = ({
  onRetry,
  t,
  ...props
}: {
  readonly onChangeNumber?: () => void;
  readonly onStartAgain?: () => void;
  readonly onRetry: () => void | Promise<void>;
  readonly showSupportContact: boolean;
  readonly t: TranslateFn;
}) => {
  const { restart, secondsRemaining } = useRetryCountdown(RETRY_DELAY_SECONDS);

  const handleRetry = () => {
    restart();
    void onRetry();
  };

  const retryLink =
    secondsRemaining > 0 ? (
      <span>{t('page.mfaSetup.code.tryAgainIn', { seconds: secondsRemaining })}</span>
    ) : (
      <InlineLinkButton onClick={handleRetry}>{t('page.mfaSetup.code.tryAgain')}</InlineLinkButton>
    );
  const secondLink = getSecondLink({ ...props, t });

  const helpKey = secondLink ? 'page.mfaSetup.code.helpPrefixWithSecondLink' : 'page.mfaSetup.code.helpPrefix';

  return (
    <Text>
      <Trans components={{ retryLink, ...(secondLink ? { secondLink } : undefined) }} i18nKey={helpKey} />
    </Text>
  );
};

type CodeCaptureFormProps = BaseCaptureFormProps & {
  readonly inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  readonly onChangeNumber?: () => void;
  readonly onRetry?: () => void | Promise<void>;
  readonly onStartAgain?: () => void;
  readonly showSupportContact?: boolean;
};

const CodeCaptureForm = ({
  autoComplete,
  buttonLabel,
  description,
  errorRequired,
  inputLabel,
  inputMode,
  onChangeNumber,
  onRetry,
  onStartAgain,
  onSubmit,
  placeholder,
  title,
  showSupportContact = false,
}: CodeCaptureFormProps) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<{ value: string }>({ defaultValues: { value: '' } });

  const submitHandler = handleSubmit(
    createSubmitHandler({
      onSubmit: async ({ value }) => onSubmit(value.trim()),
      setError,
    })
  );

  const errorMessages = useCollectedErrors(errors);
  const id = useId();

  return (
    <FormLayout
      buttonLabel={buttonLabel}
      description={description}
      errorMessages={errorMessages}
      isSubmitting={isSubmitting}
      submitHandler={submitHandler}
      title={title}
    >
      {inputLabel ? <Label htmlFor={id}>{inputLabel}</Label> : null}
      <Input
        {...register('value', { required: errorRequired })}
        autoComplete={autoComplete}
        id={id}
        inputMode={inputMode}
        placeholder={placeholder}
        variant={errorMessages.length > 0 ? 'error' : 'default'}
      />
      {onRetry ? (
        <HaventReceivedCodeHelpText
          onChangeNumber={onChangeNumber}
          onRetry={onRetry}
          onStartAgain={onStartAgain}
          showSupportContact={showSupportContact}
          t={t}
        />
      ) : null}
    </FormLayout>
  );
};

export { CodeCaptureForm, HaventReceivedCodeHelpText, RETRY_DELAY_SECONDS };
